import { useState, useCallback } from "react";
import { useLang } from "../../context/LanguageContext";
import { images, unitImages } from "../../lib/api";
import { Dialog, DialogContent, DialogTitle } from "../ui/dialog";
import { ChevronLeft, ChevronRight } from "lucide-react";

export default function Gallery() {
  const { t, lang } = useLang();
  const [activeIdx, setActiveIdx] = useState(-1);

  const items = [
    { src: images.living,       alt: lang === "de" ? "Wohnzimmer" : "Living room",                span: "col-span-2 row-span-2" },
    { src: images.kitchen,      alt: lang === "de" ? "Küche" : "Kitchen" },
    { src: images.bedroom1,     alt: lang === "de" ? "Schlafzimmer 1" : "Bedroom 1" },
    { src: images.exterior,     alt: lang === "de" ? "Chalet Außenansicht" : "Chalet exterior" },
    { src: images.cozyAlt,      alt: lang === "de" ? "Essbereich" : "Dining area" },
    { src: images.cozy,         alt: lang === "de" ? "Wohnzimmer Sitzecke" : "Living room nook" },
    { src: images.bedroom3,     alt: lang === "de" ? "Schlafzimmer 2" : "Bedroom 2",              span: "col-span-2" },
    { src: unitImages.zirben.gallery[6], alt: lang === "de" ? "Badezimmer" : "Bathroom" },
    { src: images.garden,       alt: lang === "de" ? "Terrasse & Garten" : "Terrace & garden" },
  ];

  const prev = useCallback(() => setActiveIdx(i => (i - 1 + items.length) % items.length), [items.length]);
  const next = useCallback(() => setActiveIdx(i => (i + 1) % items.length), [items.length]);

  return (
    <section id="gallery" data-testid="gallery-section" className="section-pad bg-[hsl(var(--primary))]">
      <div className="container-x">
        <div className="max-w-2xl mb-14">
          <div className="overline mb-5 !text-[hsl(var(--accent))]">{t.gallery.eyebrow}</div>
          <h2 className="font-serif font-light text-4xl sm:text-5xl md:text-6xl leading-[1.02] tracking-tight text-white whitespace-pre-line">
            {t.gallery.title}
          </h2>
          <p className="mt-6 text-base md:text-lg text-white/80">{t.gallery.sub}</p>
        </div>

        <div className="grid grid-cols-2 lg:grid-cols-4 auto-rows-[180px] md:auto-rows-[240px] gap-3 md:gap-4">
          {items.map((it, i) => (
            <button
              key={it.src}
              onClick={() => setActiveIdx(i)}
              data-testid={`gallery-item-${i}`}
              className={`overflow-hidden bg-[hsl(var(--muted))] group relative ${it.span || ""}`}
            >
              <img src={it.src} alt={it.alt} className="w-full h-full object-cover gallery-img" />
              <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors duration-300" />
              <span className="absolute bottom-3 left-3 text-xs text-white/0 group-hover:text-white/90 transition-colors duration-300 tracking-wide uppercase font-medium">
                {it.alt}
              </span>
            </button>
          ))}
        </div>

        <div className="mt-6 text-center">
          <span className="text-xs tracking-[0.18em] uppercase text-white/50">
            {items.length} {lang === "de" ? "Fotos" : "photos"} · {lang === "de" ? "Klicken zum Vergrößern" : "Click to enlarge"}
          </span>
        </div>
      </div>

      <Dialog open={activeIdx >= 0} onOpenChange={(o) => !o && setActiveIdx(-1)}>
        <DialogContent className="max-w-6xl !rounded-none p-0 border-none bg-black/95 backdrop-blur-xl" aria-describedby={undefined}>
          <DialogTitle className="sr-only">Gallery</DialogTitle>
          {activeIdx >= 0 && (
            <div className="relative flex items-center justify-center min-h-[60vh]">
              <img
                src={items[activeIdx]?.src}
                alt={items[activeIdx]?.alt}
                className="max-h-[85vh] w-auto max-w-full object-contain"
              />
              <button
                onClick={prev}
                data-testid="gallery-prev"
                className="absolute left-2 md:left-4 top-1/2 -translate-y-1/2 w-10 h-10 flex items-center justify-center bg-white/10 hover:bg-white/25 text-white transition-colors"
              >
                <ChevronLeft className="w-6 h-6" />
              </button>
              <button
                onClick={next}
                data-testid="gallery-next"
                className="absolute right-2 md:right-4 top-1/2 -translate-y-1/2 w-10 h-10 flex items-center justify-center bg-white/10 hover:bg-white/25 text-white transition-colors"
              >
                <ChevronRight className="w-6 h-6" />
              </button>
              <div className="absolute bottom-4 left-0 right-0 text-center">
                <span className="text-white/80 text-sm">{items[activeIdx]?.alt}</span>
                <span className="text-white/40 text-xs ml-3">{activeIdx + 1} / {items.length}</span>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </section>
  );
}
